import React from 'react';
import { UitkTertiaryButton } from 'uitk-react-button';
import { UitkCard } from 'uitk-react-cards';
import { UitkImage, UitkFigure, UitkFigureAspectRatioType } from 'uitk-react-images';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkText } from 'uitk-react-text';
import { UitkDealMemberBadge } from 'uitk-react-badge';
import { ViewMedium, Viewport, ViewSmall } from '@shared-ui/viewport-context';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSpacing } from 'uitk-react-spacing';

interface Link {
  label: string;
  href: string;
}

interface Card {
  title: string;
  description: string;
  link: Link;
}
interface Image {
  src: string;
  alt: string;
}

interface Props {
  brandName: string;
  description: string;
  link: Link;
  cards?: Card[];
  image: Image;
  invertImageLocation?: boolean;
  comingSoon?: string;
  title?: string;
}
export const BrandSection: React.FC<Props> = ({
  comingSoon,
  invertImageLocation,
  brandName,
  description,
  link,
  cards,
  image,
  title,
}) => {
  return (
    <UitkLayoutFlexItem className="home-spacing">
      <section id="brand-section">
        <UitkLayoutFlex className="marketing-page-width" direction="column" alignItems="center" wrap="wrap">
          <Viewport>
            <ViewMedium>
              <>
                <UitkLayoutFlexItem>
                  <UitkSpacing margin={{ blockend: 'sixteen' }}>
                    <UitkText size={800}>{title}</UitkText>
                  </UitkSpacing>
                </UitkLayoutFlexItem>
                <UitkLayoutFlexItem minWidth={'full_width'}>
                  <div>
                    <UitkLayoutFlex
                      direction={invertImageLocation ? 'row-reverse' : 'row'}
                      justifyContent={'space-between'}
                      space="six"
                      alignItems="center"
                    >
                      <UitkLayoutFlexItem minWidth={'half_width'}>
                        <div>
                          <UitkLayoutFlex space="six" direction="column" alignItems="start">
                            <div>
                              <UitkLayoutFlex space="two" alignItems="center">
                                <UitkText size={600}>{brandName}</UitkText>
                                {comingSoon && <UitkDealMemberBadge text={comingSoon} />}
                              </UitkLayoutFlex>
                            </div>
                            <UitkText size={500}>{description}</UitkText>
                            {link.label && (
                              <div>
                                <UitkTertiaryButton tag="a" alignLabelToContent="start" href={link.href}>
                                  {link.label}
                                </UitkTertiaryButton>
                              </div>
                            )}
                          </UitkLayoutFlex>
                        </div>
                      </UitkLayoutFlexItem>

                      <UitkLayoutFlexItem maxWidth={'half_width'}>
                        <UitkFigure imageFit="contain" ratio={UitkFigureAspectRatioType.R21_9}>
                          <UitkImage {...image} />
                        </UitkFigure>
                      </UitkLayoutFlexItem>
                    </UitkLayoutFlex>
                  </div>
                </UitkLayoutFlexItem>
              </>
            </ViewMedium>
            <ViewSmall>
              <>
                <UitkLayoutFlexItem alignSelf="start">
                  <UitkSpacing margin={{ blockstart: 'six' }}>
                    <UitkText size={600}>{title}</UitkText>
                  </UitkSpacing>
                </UitkLayoutFlexItem>
                <UitkLayoutFlexItem minWidth={'full_width'}>
                  <div>
                    <UitkLayoutFlex
                      direction={invertImageLocation ? 'row-reverse' : 'row'}
                      justifyContent={'space-between'}
                      space="six"
                      wrap={invertImageLocation ? 'wrap-reverse' : 'wrap'}
                    >
                      <UitkLayoutFlexItem minWidth={'full_width'}>
                        <div>
                          <UitkLayoutFlex space="four" direction="column" alignItems="start">
                            <div>
                              <UitkLayoutFlex space="two" alignItems="center">
                                <UitkSpacing margin={{ blockstart: 'six' }}>
                                  <UitkText size={600}>{brandName}</UitkText>
                                </UitkSpacing>
                                {comingSoon && <UitkDealMemberBadge text={comingSoon} />}
                              </UitkLayoutFlex>
                            </div>
                            <UitkText size={500}>{description}</UitkText>
                            <div>
                              <UitkTertiaryButton tag="a" alignLabelToContent="start" href={link.href}>
                                {link.label}
                              </UitkTertiaryButton>
                            </div>
                          </UitkLayoutFlex>
                        </div>
                      </UitkLayoutFlexItem>

                      <UitkLayoutFlexItem maxWidth={'full_width'}>
                        <UitkFigure imageFit="contain" ratio={UitkFigureAspectRatioType.R21_9}>
                          <UitkImage {...image} />
                        </UitkFigure>
                      </UitkLayoutFlexItem>
                    </UitkLayoutFlex>
                  </div>
                </UitkLayoutFlexItem>
              </>
            </ViewSmall>
          </Viewport>
          {cards && (
            <UitkSpacing margin={{ blockstart: 'twelve' }}>
              <UitkLayoutGrid columns={{ small: ['1fr'], medium: ['1fr', '1fr', '1fr'] }} space="six">
                {cards.map((card) => (
                  <UitkLayoutGridItem key={card.title}>
                    <UitkCard border padded>
                      <UitkLayoutFlex space="two" direction="column" alignItems="start">
                        <UitkText size={600} theme="default">
                          {card.title}
                        </UitkText>
                        <UitkText size={300} theme="default">
                          {card.description}
                        </UitkText>
                        <div>
                          <UitkTertiaryButton tag="a" alignLabelToContent="start" href={card.link.href}>
                            {card.link.label}
                          </UitkTertiaryButton>
                        </div>
                      </UitkLayoutFlex>
                    </UitkCard>
                  </UitkLayoutGridItem>
                ))}
              </UitkLayoutGrid>
            </UitkSpacing>
          )}
        </UitkLayoutFlex>
      </section>
    </UitkLayoutFlexItem>
  );
};

BrandSection.displayName = 'BrandSection';
